import { api, Banner, dateTime, Empty, stackOnMobile, useAsync } from "@getaways/extranet-ui";
import { BRANDS, eur, STATUS } from "./format";

interface OrderLine {
  orderId: string;
  reference: string;
  brand: string;
  status: string;
  bookedAt: string;
  departureDate: string | null;
  agentEmail: string | null;
  agentName: string | null;
  sellEurMinor: number | null;
  paidEurMinor: number | null;
  outstandingEurMinor: number | null;
}

interface ServiceLine {
  orderId: string;
  itemId: string;
  name: string;
  vertical: string;
  supplier: string;
  status: string;
  serviceStart: string | null;
  supplierReference: string | null;
  brand: string;
}

const qs = (params: Record<string, string | undefined>) => new URLSearchParams(Object.entries(params).filter((e): e is [string, string] => !!e[1])).toString();

/** Orders matching a filter, as a short list (operations and finance work lists). */
export function OrderList({ title, params, money = false, empty }: { title: string; params: Record<string, string | undefined>; money?: boolean; empty: string }) {
  const q = qs({ limit: "50", ...params });
  const { data, error } = useAsync(() => api<{ rows: OrderLine[]; total: number }>("GET", `/v1/bookings?${q}`), [q]);
  return (
    <section className="card" style={{ padding: 0 }}>
      <div className="row" style={{ padding: "12px 14px", borderBottom: "1px solid var(--line)" }}><h2 style={{ margin: 0, fontSize: 15 }}>{title}</h2><span className="spacer" />{data && <span className="small muted">{data.total}</span>}</div>
      {error && <Banner notice={{ tone: "bad", text: error }} />}
      {data && data.rows.length === 0 ? <Empty>{empty}</Empty> : (
        <table ref={stackOnMobile} className="table stack-mobile">
          <thead><tr><th>Reference</th><th>Brand</th><th>Status</th><th>Departs</th><th>Agent</th><th className="num">Sold</th>{money && <><th className="num">Paid</th><th className="num">To pay</th></>}</tr></thead>
          <tbody>
            {(data?.rows ?? []).map((o) => (
              <tr key={o.orderId}>
                <td className="mono">{o.reference}<div className="small muted">booked {dateTime(o.bookedAt, "Europe/Lisbon")}</div></td>
                <td className="small">{BRANDS[o.brand] ?? o.brand}</td>
                <td className="small">{STATUS[o.status] ?? o.status}</td>
                <td className="small">{o.departureDate ?? "—"}</td>
                <td className="small">{o.agentName ?? o.agentEmail ?? "Site"}</td>
                <td className="num">{eur(o.sellEurMinor)}</td>
                {money && <><td className="num">{eur(o.paidEurMinor)}</td><td className="num">{eur(o.outstandingEurMinor)}</td></>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/** Services matching a filter: what the supplier has not confirmed yet, say. */
export function ServiceList({ title, params, empty }: { title: string; params: Record<string, string | undefined>; empty: string }) {
  const q = qs({ limit: "50", sort: "serviceStart", dir: "asc", ...params });
  const { data, error } = useAsync(() => api<{ rows: ServiceLine[]; total: number }>("GET", `/v1/items?${q}`), [q]);
  return (
    <section className="card" style={{ padding: 0 }}>
      <div className="row" style={{ padding: "12px 14px", borderBottom: "1px solid var(--line)" }}><h2 style={{ margin: 0, fontSize: 15 }}>{title}</h2><span className="spacer" />{data && <span className="small muted">{data.total}</span>}</div>
      {error && <Banner notice={{ tone: "bad", text: error }} />}
      {data && data.rows.length === 0 ? <Empty>{empty}</Empty> : (
        <table ref={stackOnMobile} className="table stack-mobile">
          <thead><tr><th>Service</th><th>Starts</th><th>Supplier</th><th>Status</th><th>Brand</th></tr></thead>
          <tbody>
            {(data?.rows ?? []).map((s) => (
              <tr key={`${s.orderId}:${s.itemId}`}>
                <td>{s.name}<div className="small muted">{s.vertical}</div></td>
                <td className="small">{s.serviceStart ?? "—"}</td>
                <td className="small">{s.supplier}{s.supplierReference && <div className="muted mono">{s.supplierReference}</div>}</td>
                <td className="small">{s.status}</td>
                <td className="small">{BRANDS[s.brand] ?? s.brand}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
